import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";

interface Props {
  Menuprops: menuInfo;
}

const SN_PMS_BAS_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const { userInfo } = global(); // Login user 정보
  const searchRef = useRef<any>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRows = getGridCurrent("grid1").getChange();

    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();

      const params = {
        id: "sn_pms_bas_reg_004_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1],
        grid3: data[2],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      getGridCurrent("grid3").stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const requiredValuesCheckL1 =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL1) {
        return false;
      }

      const requiredValuesCheckL2 =
        getGridCurrent("grid3").requiredValuesCheck();
      if (!requiredValuesCheckL2) {
        return false;
      }

      const budgetcodehChanges = getGridCurrent("grid1").getChange();
      const budgetcodelChanges = getGridCurrent("grid2").getChange();
      const budgetcode_laborChanges = getGridCurrent("grid3").getChange();

      if (
        budgetcodehChanges.modifiedRowcount === 0 &&
        budgetcodelChanges.modifiedRowcount === 0 &&
        budgetcode_laborChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 중복체크
      const cdBudget: any[] = [];
      getGridCurrent("grid1").forEachNode((node: any) => {
        cdBudget.push(node.data.cd_budget);
      });

      const dupBudget = cdBudget.filter(
        (item, idx) => cdBudget.indexOf(item) !== idx
      );

      if (dupBudget.length > 0) {
        await showDialog(
          "alter",
          `중복된 예산코드가 존재합니다: [${[...new Set(dupBudget)].join(", ")}]`
        );
        return;
      }

      const existingcdAcct: any[] = [];
      getGridCurrent("grid2").forEachNode((node) => {
        if (node.data) {
          existingcdAcct.push(node.data);
        }
      });

      const dupAcct = existingcdAcct.filter((item, idx) => {
        return (
          existingcdAcct.findIndex(
            (target, targetIdx) =>
              targetIdx < idx && // 나보다 앞에 동일한 조합이 있는지 확인
              target.cd_acct === item.cd_acct &&
              target.cd_budget === item.cd_budget
          ) !== -1
        );
      });

      if (dupAcct.length > 0) {
        const formattedDups = dupAcct.map(
          (item) => `[${item.cd_budget}][${item.cd_acct}]`
        );

        const uniqueDups = [...new Set(formattedDups)].join(", ");

        await showDialog(
          "alter",
          `중복된 계정과목정보가 존재합니다 [(예산코드][계정과목]): ${uniqueDups}`
        );
        return;
      }

      const existingcdEmp: any[] = [];
      getGridCurrent("grid3").forEachNode((node) => {
        if (node.data) {
          existingcdEmp.push(node.data);
        }
      });

      const dupEmp = existingcdEmp.filter((item, idx) => {
        return (
          existingcdEmp.findIndex(
            (target, targetIdx) =>
              targetIdx < idx && // 나보다 앞에 동일한 조합이 있는지 확인
              target.cd_emp === item.cd_emp &&
              target.cd_budget === item.cd_budget
          ) !== -1
        );
      });

      if (dupEmp.length > 0) {
        const formattedDups = dupEmp.map(
          (item) => `[${item.cd_budget}][${item.cd_emp}]`
        );

        const uniqueDups = [...new Set(formattedDups)].join(", ");

        await showDialog(
          "alter",
          `중복된 사원정보가 존재합니다 ([예산코드][사원코드]): ${uniqueDups}`
        );
        return;
      }

      // 요청 데이터 구성
      const SaveData = {
        budgetcodeh: budgetcodehChanges,
        budgetcodel: budgetcodelChanges,
        budgetcode_labor: budgetcode_laborChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_BAS_REG_004_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region GridEventsH
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        fg_pjt: "top1",
        fg_budget: "top1",
        tp_budget: "top1",
        fg_mgmt: "top1",
        yn_use: "Y",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region GridEventsL
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_budget: focusedNodeH.data?.cd_budget,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridBtnsClickL = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid2").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid2").getDisplayedRowCount() > 0) {
          getGridCurrent("grid2").deleteSelectedRows();
        }
        break;
    }
  };
  //#endregion

  //#region GridEventsL2
  const onBeforeAddRowL2 = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_budget: focusedNodeH.data?.cd_budget,
        um_labor: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridBtnsClickL2 = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid3").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid3").getDisplayedRowCount() > 0) {
          getGridCurrent("grid3").deleteSelectedRows();
        }
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGridBtnsClickL,
          onBtnDeleteClick: onGridBtnsClickL,
          onBeforeAddRow: onBeforeAddRowL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBtnAddClick: onGridBtnsClickL2,
          onBtnDeleteClick: onGridBtnsClickL2,
          onBeforeAddRow: onBeforeAddRowL2,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="50%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "예산코드정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["cd_budget"],
            }}
          />
        </SnResizeBar>
        <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
          <SnResizeBar direction={"height"} id="panel2" orgSize="50%">
            <SnGrid
              ref={gridRefs.current["grid2"]} // Grid Name
              columnConfig={grids.grid2.columns} // 컬럼 설정 전달
              rowData={grids.grid2.dataSource} // Datasource
              etcProps={{ rowSelection: "single", title: "계정과목정보" }}
              events={grids.grid2.events}
            />
          </SnResizeBar>
          <div className="flex-1 min-h-0">
            <SnGrid
              ref={gridRefs.current["grid3"]} // Grid Name
              columnConfig={grids.grid3.columns} // 컬럼 설정 전달
              rowData={grids.grid3.dataSource} // Datasource
              etcProps={{ rowSelection: "single", title: "노무비정보" }}
              events={grids.grid3.events}
            />
          </div>
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PMS_BAS_REG_004;
